import { useToastController, Toast, ToastTitle, ToastBody } from '@fluentui/react-components';
import { createElement, useCallback } from 'react';

/**
 * The single, app-wide toaster id. ONE `<Toaster>` is mounted (in TaskPaneShell)
 * and every notification targets this id: a per-component `useId` would target an
 * id no mounted Toaster uses, and the toast would silently never appear.
 *
 * Historically this belonged to the mail-filing flow (hence the value's name);
 * it is now the shared channel for any transient message.
 */
export const APP_TOASTER_ID = 'prism-file-mail-toaster';

/**
 * Generic transient notifications.
 *
 * Exists because mutation failures were being dropped on the floor: a mutation
 * whose `onError` nobody wired left the user staring at an unchanged UI with no
 * idea the server had refused (a duplicate folder name did exactly that). Any
 * mutation that can fail for a reason the user can act on should say so here.
 */
export function useNotify() {
  const { dispatchToast } = useToastController(APP_TOASTER_ID);

  const notify = useCallback(
    (intent: 'success' | 'warning' | 'error' | 'info', title: string, body?: string): void => {
      dispatchToast(
        createElement(
          Toast,
          null,
          createElement(ToastTitle, null, title),
          body ? createElement(ToastBody, null, body) : null,
        ),
        { intent, timeout: intent === 'error' ? 10000 : 5000 },
      );
    },
    [dispatchToast],
  );

  const notifyError = useCallback(
    (title: string, body?: string) => notify('error', title, body),
    [notify],
  );

  const notifyWarning = useCallback(
    (title: string, body?: string) => notify('warning', title, body),
    [notify],
  );

  return { toasterId: APP_TOASTER_ID, notify, notifyError, notifyWarning };
}
